/**
 * Public size availability block shown on the product detail page.
 *
 * - Available / Preorder sizes are clickable visual chips (selection only —
 *   no checkout wired yet).
 * - Sold-out sizes are rendered disabled with "Sold Out".
 * - Hidden sizes never reach the client (server strips them) so this
 *   component only renders what the user is allowed to see.
 */
import { useState } from "react";

const STATUS_COPY = {
  available: { label: "In stock", tone: "ok" },
  preorder: { label: "Preorder", tone: "preorder" },
  sold_out: { label: "Sold Out", tone: "off" },
};

export default function SizeAvailability({ sizes, sizeType }) {
  const [selected, setSelected] = useState(null);

  // Defensive: ensure we only ever render the four public-safe statuses.
  const visible = (Array.isArray(sizes) ? sizes : []).filter(
    (s) => s && s.size && s.status !== "hidden",
  );

  if (visible.length === 0) {
    return null;
  }

  const isShoe = sizeType === "shoe";
  const selectorLabel = isShoe ? "Available Shoe Sizes" : "Select Size";

  const selectedRow = visible.find((s) => s.size === selected);
  const statusLine = selectedRow
    ? STATUS_COPY[selectedRow.status]?.label || selectedRow.status
    : "Tap a size to view status";

  return (
    <div
      data-testid="size-availability"
      data-size-type={isShoe ? "shoe" : "clothing"}
      className="border-t border-[#1a1a1a] pt-6"
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#ff1493]">
          // {selectorLabel}
        </div>
        {selected && (
          <div
            data-testid="size-availability-status"
            className={`font-mono text-[10px] tracking-[0.22em] uppercase ${
              selectedRow?.status === "sold_out"
                ? "text-[#ff3b30]"
                : selectedRow?.status === "available"
                ? "text-[#00ff41]"
                : "text-[#c084fc]"
            }`}
          >
            {selected} · {statusLine}
          </div>
        )}
      </div>

      <div className="mt-3 flex flex-wrap gap-2" data-testid="size-availability-pills">
        {visible.map((s) => {
          const isSoldOut = s.status === "sold_out";
          const isSelected = selected === s.size;
          return (
            <button
              key={s.size}
              type="button"
              data-testid={`size-pill-${s.size}`}
              data-status={s.status}
              disabled={isSoldOut}
              aria-pressed={isSelected}
              onClick={() => setSelected(s.size)}
              className={[
                "min-w-[3.5rem] px-3 py-2 border font-mono text-[12px] tracking-[0.18em] uppercase transition-colors",
                isSoldOut
                  ? "border-[#2a1a3a] text-[#5a4a70] line-through cursor-not-allowed"
                  : isSelected
                  ? "border-[#ff1493] text-white bg-[#ff1493]/15"
                  : "border-[#2a1a3a] text-[#c8b8e0] hover:border-[#ff1493]/60 hover:text-white",
              ].join(" ")}
            >
              {s.size}
            </button>
          );
        })}
      </div>

      <p className="mt-3 font-mono text-[11px] leading-relaxed text-[#7a6a90]">
        // Sizing varies by product. Check available sizes before preorder access opens.
      </p>
    </div>
  );
}
